import { useState, useEffect, Fragment } from "react";
import MainLayout from "Layouts/MainLayout/MainLayout";
import Table from "Components/Common/Table/Table";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import { DateRangePicker } from "Components/Common/DateRangePicker/DateRangePicker";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { useMarketingDashboard, useConversions, useConversionsList, useAdInfo, usePostbackControls, usePostbackLog, usePostbackRuns, useTouchRepair, useFollowUpOffer, useQuizFunnel } from "CustomHooks/useConversions";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { AttributionPanel } from "./AttributionPanel";
import { TrafficPanel } from "./TrafficPanel";
import ClassNames from "Helpers/Common";
import "Colors.css";

const GROUP_OPTIONS = [
  { value: "channel", label: "Channel" },
  { value: "campaign", label: "Campaign" },
  { value: "content", label: "Ad (utm_content)" },
  { value: "source", label: "Source (raw utm)" },
];

// Ad images are deployed to the public marketing CDN by deploy-marketing.sh
// with this exact naming convention (utm_content is the creative code), so
// previews need no ad-platform API. Broken URLs (text/google ads) hide
// themselves via onError.
const AD_IMAGE_CDN = "https://ai.instacured.com/marketing";
const adImageUrl = (utmContent) =>
  utmContent ? `${AD_IMAGE_CDN}/${utmContent}_1080x1350.png` : null;

// Creatives are 1080x1350, so the hover preview renders at half size. It is
// clamped to the viewport on both axes for small screens, keeping the 4:5
// aspect ratio (width drives, height follows).
const AD_IMAGE_W = 1080;
const AD_IMAGE_H = 1350;
const HOVER_SCALE = 0.5;
const HOVER_GAP = 16;

function hoverPreviewGeometry(clientX, clientY) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxHeight = vh - HOVER_GAP * 2;
  // Half size, unless that would not fit beside the cursor or vertically.
  const width = Math.min(
    AD_IMAGE_W * HOVER_SCALE,
    vw * 0.45,
    maxHeight * (AD_IMAGE_W / AD_IMAGE_H),
  );
  const height = width * (AD_IMAGE_H / AD_IMAGE_W);

  // Prefer the right of the cursor; flip left when it would overflow.
  let left = clientX + HOVER_GAP;
  if (left + width + HOVER_GAP > vw) left = clientX - width - HOVER_GAP;
  left = Math.max(HOVER_GAP, left);

  // Vertically centred on the cursor, clamped to the viewport.
  let top = clientY - height / 2;
  top = Math.max(HOVER_GAP, Math.min(top, vh - height - HOVER_GAP));

  return { left, top, width, maxHeight };
}

// What may be sent back to each channel's ad platform, and how. Postbacks
// are not wired yet — this documents the policy the (planned) nightly job
// will enforce: server APIs for Meta/TikTok (CSV uploads are dead), a
// scheduled Google Sheet import for Google, and NOTHING for organic, owned,
// or condition-campaign traffic. First-party data in this dashboard is
// always complete regardless of postback policy.
const CHANNEL_POLICY = {
  facebook: { label: "API (Meta CAPI) · nightly · neutral campaigns only", tip: "Registrations realtime via pixel; purchases via nightly server events. Never from condition campaigns." },
  instagram: { label: "API (Meta CAPI) · nightly · neutral campaigns only", tip: "Same rules as Facebook — one Meta pipe." },
  tiktok: { label: "API (Events API) · nightly · neutral only (not connected)", tip: "Adapter not built yet; same neutral-campaign rule will apply." },
  "google-ads": { label: "Sheet import (OCI) · daily · neutral campaigns only", tip: "Google reads a sheet of gclid + conversion rows on a daily schedule." },
  "bing-ads": { label: "API/import · not connected", tip: "Same neutral-campaign rule will apply." },
  "google-seo": { label: "None (organic)", tip: "Nothing to post back — no ad platform involved." },
  "bing-seo": { label: "None (organic)", tip: "Nothing to post back." },
  "other-seo": { label: "None (organic)", tip: "Nothing to post back." },
  "instacured-ai": { label: "None (owned property)", tip: "Internal traffic from the AI checker — first-party only." },
  "instacured-blog": { label: "None (owned property)", tip: "Internal traffic — first-party only." },
  "instacured-internal": { label: "None (owned property)", tip: "Internal traffic — first-party only." },
  email: { label: "None (owned channel)", tip: "Email is first-party; no postbacks." },
  referral: { label: "None", tip: "No ad platform involved." },
  direct: { label: "None", tip: "No attribution source — nothing to post back." },
};
const policyFor = (key) =>
  CHANNEL_POLICY[key || "direct"] || { label: "None (unmapped source)", tip: "Tagged source without a postback integration." };

const TABS = [
  { key: "all", label: "All Channels" },
  // Catch-all: rows whose FIRST-touch channel has no tab of its own
  // (direct visits, untagged referrers, ...) — so no row can silently
  // vanish from every tab. Backend inverts the filter for this key.
  { key: "other", label: "Direct" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "google", label: "Google" },
  { key: "seo", label: "SEO" },
  { key: "blog", label: "Blog" },
  // Offline QR entry points (/qr-download stamps utm_source=qr → channel "qr")
  { key: "qr", label: "QR Code" },
  // Traffic attributed to the AI checker (its query string / referrer →
  // channel "instacured-ai"). CHANGED 2026-08-24: direct visits to
  // ai.instacured.com belong here now. They used to produce no touch at all,
  // fell through to "direct" and landed in the "Direct" catch-all, which
  // under-counted the checker's own visitors; the checker now self-identifies
  // with utm_source=instacured-ai when nothing else is found. A real Facebook
  // or Google click landing there keeps its own source and tab. Rows created
  // before 08-24 were not backfilled and stay under "Direct".
  { key: "ai", label: "AI Site" },
  // Whole-site traffic, not a channel: pageviews and unique visitors from our
  // own beacon. This is where Beam Analytics' numbers used to live before the
  // vendor dissolved; there is no third-party analytics on the site any more.
  { key: "traffic", label: "Traffic" },
  { key: "postbacks", label: "Postbacks" },
  // Diagnostic rather than a channel: where paid conversions lose their click
  // id. Lives here because it answers the question the other tabs raise.
  { key: "attribution", label: "Attribution" },
];

// Summary rows are keyed by CHANNEL (classifyChannel values); tabs use their
// own keys. Map every channel that has a home tab so its row deep-links there
// — before this map only same-named pairs (facebook, instagram, qr) linked.
const CHANNEL_TO_TAB = {
  facebook: "facebook",
  instagram: "instagram",
  "google-ads": "google",
  "google-seo": "seo",
  "bing-seo": "seo",
  "other-seo": "seo",
  "instacured-blog": "blog",
  qr: "qr",
  "instacured-ai": "ai",
};

// Every channel WITHOUT a home tab (direct, referral, email, tiktok, ...)
// lives in the "other" tab — that tab is the backend's inversion of all the
// named ones, so this fallback can never point at an empty view.
const tabForChannel = (channel) => CHANNEL_TO_TAB[channel] || "other";

const PER_PAGE = 25;

// Mirrors FOLLOW_UP_DELAY_HOURS in the backend's followUpOffer.service.ts —
// display only; the backend value is the one that selects candidates.
const FOLLOWUP_DELAY_LABEL = "2h";

// Tabs that get the campaign filter above their detail table. These are the
// paid tabs: they are the only ones where "which campaign" is the question
// being asked. The campaign names come from the same funnel aggregation the
// All Channels table runs, scoped to the tab's channel (backend `tab` param)
// and grouped by campaign.
const CAMPAIGN_FILTER_TABS = ["facebook", "instagram", "google"];

// Tags shown before the "+N more" toggle. Meta accounts routinely carry dozens
// of campaigns, most with a handful of pre-registrations; showing them all by
// default buries the ones that matter under a wall of dead names.
const CHIPS_COLLAPSED = 16;

// ---- Sortable / resizable column machinery ----
// `value` extracts the sortable value per row; numeric columns start
// descending on first click (Google Ads convention). Sorting the detail
// tabs is client-side over the current page.
const SUMMARY_COLS = [
  { key: "name", label: "Name", value: (r) => (r.key || "(direct / unknown)").toLowerCase() },
  {
    key: "impressions", label: "Impressions", numeric: true, value: (r) => r.impressions ?? 0,
    info:
      "Unique landing-page arrivals: one per visitor, per page, per day, counted only for URLs carrying a query string (every ad click does). " +
      "Reloads and back-navigation do not add to it. Hover a cell for the number of distinct PEOPLE behind those impressions — one visitor reading three pages is three impressions and one person. " +
      "Only counts arrivals since the impression beacon shipped \u2014 a range before that shows 0 because nothing was counting, not because nobody came.",
  },
  { key: "preRegistered", label: "Pre-Registered", numeric: true, value: (r) => r.preRegistered ?? 0 },
  {
    key: "bounce", label: "Bounce %", numeric: true, value: (r) => bounceRate(r) ?? -1,
    info:
      "Share of the people who landed and left without giving an email: (visitors − pre-registered) / visitors. " +
      "Measured against distinct PEOPLE, not impressions — a pre-registration is per human, so counting it against page views would understate it. " +
      "Blank when nothing was counted for the row. It can read 0% if someone pre-registered on a visit that carried no query string (a typed URL, an app link), since that visit was never counted as an impression.",
  },
  { key: "verified", label: "Verified", numeric: true, value: (r) => r.verified ?? 0 },
  { key: "registered", label: "Registered", numeric: true, value: (r) => r.registered ?? 0 },
  { key: "paidPatients", label: "Paid Patients", numeric: true, value: (r) => r.paidPatients ?? 0 },
  {
    key: "offerSent", label: "Offer Sent", numeric: true, value: (r) => r.offerSent ?? 0,
    info: "Patients who registered, never opened a visit, and were sent the 10%-off follow-up (email + push). Counts the nudge, not the outcome.",
  },
  {
    key: "paidAfterOffer", label: "Paid After Offer", numeric: true, value: (r) => r.paidAfterOffer ?? 0,
    info:
      "Of those nudged, how many paid for a visit AFTER the nudge went out. " +
      "Read it as correlation, not proof: these patients were picked precisely because they had stalled, and some share would have returned on their own. " +
      "Without a holdout group this measures \"paid after we asked\", not \"paid because we asked\" \u2014 compare it against the stall rate before the job was switched on.",
  },
  { key: "revenue", label: "Revenue", numeric: true, value: (r) => r.revenue ?? 0 },
  { key: "conv", label: "Conversion %", numeric: true, value: (r) => (r.preRegistered ? r.paidPatients / r.preRegistered : 0) },
];
// Meta spend columns, appended only when the backend has Meta credentials.
// Spend is merged onto the funnel rows before sorting (campaign/content
// groupings have join keys; other groupings show an em-dash), so rows
// without spend sort below any matched row on the first (descending) click.
const SPEND_COLS = [
  {
    key: "spend", label: "Spend", numeric: true, value: (r) => r.spend ?? -1,
    info: "Meta ad spend for the range, ingested nightly from the Marketing API. Matched to rows by the Meta ids captured on ad clicks, else by exact campaign-name = utm_campaign match; spend matching no row appears as \"(unmatched Meta spend)\".",
  },
  {
    key: "cpa", label: "CPA", numeric: true, value: (r) => r.cpa ?? -1,
    info: "Spend / paid patients. Totals row: all Meta spend / paid patients of Meta-matched campaigns.",
  },
  {
    key: "roas", label: "ROAS", numeric: true, value: (r) => r.roas ?? -1,
    info: "Revenue / spend. Totals row: Meta-matched revenue / all Meta spend.",
  },
];

const SUMMARY_POSTBACK_COL = {
  key: "postback",
  label: "Postback",
  sortable: false,
  info:
    "Postback column shows what the (planned) nightly job may send to each platform. Condition-campaign conversions are never posted back anywhere; this dashboard always shows the complete first-party numbers either way.",
};

const DETAIL_COLS = [
  { key: "created", label: "Created", numeric: true, value: (r) => new Date(r.createdAt ?? 0).getTime() },
  { key: "email", label: "Email", value: (r) => r.username ?? "" },
  // Campaign ID / Ad / Ad ID collapsed INTO the Campaign cell as sub-lines
  // (Omer 2026-08-23) — one column, campaign name on top.
  { key: "preview", label: "Preview", sortable: false },
  // Device and browser share one column: they are only useful read together
  // (an "iPhone" row means something different in Safari than in the Facebook
  // in-app browser). Sorts device-then-browser so the populations group.
  // See parseUA for what this can and cannot tell apart.
  {
    key: "device",
    label: "Device / Browser",
    value: (r) => { const u = parseUA(uaOf(r)); return u ? `${u.device} ${u.browser}` : ""; },
    info: "Device and browser from the FIRST touch's user agent — the visit that actually carried the ad click id. Heuristic: Brave reports itself as Chrome, and iPadOS reports itself as Mac. Hover a cell for the raw user agent.",
  },
  {
    key: "journey",
    label: "Journey",
    sortable: false,
    info:
      "Every page this person viewed on the marketing site, oldest first, joined to the registration by the browser's visitor cookie (rows since the column shipped) or, failing that, by a gclid/fbclid on the touch (older paid rows). Google's gbraid/wbraid are never used: one value is shared by every iPhone click on the same ad, so it would merge strangers' sessions. " +
      "Hover a page for when it was first and last viewed and how many times. A date marker appears where the visit crossed into a new day. Blank when no pageview could be matched \u2014 app signups never had a website visit.",
  },
  { key: "status", label: "Status", numeric: true, value: (r) => (r.paidVisits > 0 ? 3 : r.registered ? 2 : r.emailVerified ? 1 : 0) },
  {
    key: "delivery",
    label: "Delivery",
    sortable: false,
    info: "Per-platform postback delivery of the first-party conversion event: ✓ reached the platform, ✗ send failed (hover for error), … eligible but not sent yet, – not eligible. Hover any chip for the reason and for the channel/campaign eligibility was judged on — that is the most recent touch carrying an ad click id, which is not always the touch this row displays.",
  },
  { key: "params", label: "Params", sortable: false },
];

// Where the traffic actually came from. Only rendered on the Direct tab (the
// catch-all): every other tab already knows its channel, and the ad columns
// answer the question there. Direct is the one tab where "what IS this?" is
// unanswered by the tab itself.
const SOURCE_COL = {
  key: "source",
  label: "Source / Referrer",
  value: (r) => { const s = sourceInfo(r); return s ? `${s.channel} ${s.host}` : "zzz"; },
  info:
    "Referrer and landing page of the FIRST touch, captured first-party on the landing page. " +
    "\"app signup\" = created by the app's email-first signup, so there was never a website visit to attribute (organic installs, word of mouth) \u2014 " +
    "the single biggest reason this tab is large. " +
    "\"web \u00b7 no touch\" = pre-registered on the marketing site but nothing was captured: no query params AND no external referrer. " +
    "\"direct (no referrer)\" = a real visit whose browser sent no Referer header (typed/bookmarked URLs, most app and messenger links, https\u2192http hops) \u2014 a browser limit, not missing capture.",
};

// Direct traffic has no creative, so the ad Preview column is dead weight
// there — the Source column takes its slot rather than widening the table.
const detailColsFor = (tab) =>
  tab === "other"
    ? DETAIL_COLS.map((c) => (c.key === "preview" ? SOURCE_COL : c))
    : DETAIL_COLS;

// Postback delivery chip for one platform, driven by the first-party
// ConversionEvent record (source of truth) — not by the ad platform.
//
// `judged` is the channel/campaign the backend actually decided eligibility
// on: the most recent touch carrying a click id, which is NOT always the
// touch shown in this row. A visitor who clicked a Facebook ad and later
// returned organically is filed in the Facebook tab (rows are bucketed by
// FIRST touch) while eligibility is judged on whichever touch held the click
// id. When those disagree, a "–" here looks inexplicable, so the tooltip
// spells out what it was judged on instead of just saying "not eligible".
const DeliveryChip = ({ label, p, judged }) => {
  const on = judged?.channel || judged?.utmCampaign
    ? ` — judged on ${judged.channel ?? "unknown channel"}${judged.utmCampaign ? ` / ${judged.utmCampaign}` : " / no campaign"}`
    : "";
  let cls = "bg-gray-100 text-gray-400";
  let mark = "–";
  // Campaign present but not eligible = the neutral-campaign compliance gate;
  // no campaign at all = no click id survived to be matched on.
  let tip = (judged?.utmCampaign
    ? "Not eligible: campaign is not on the neutral-campaign allowlist, so it is never posted back"
    : "Not eligible: no ad click id captured for this conversion") + on;
  if (p?.eligible) {
    if (p.sentAt) { cls = "bg-green-100 text-green-700"; mark = "✓"; tip = `Delivered ${new Date(p.sentAt).toLocaleString()}${on}`; }
    else if (p.error) { cls = "bg-red-100 text-red-700"; mark = "✗"; tip = p.error + on; }
    else { cls = "bg-amber-100 text-amber-700"; mark = "…"; tip = "Eligible — not sent yet" + on; }
  }
  return (
    <span title={tip} className={ClassNames("inline-block px-1.5 py-0.5 mr-1 rounded-full text-xs font-medium", cls)}>
      {label} {mark}
    </span>
  );
};

const sortRows = (rows, cols, sort) => {
  if (!sort) return rows;
  const col = cols.find((c) => c.key === sort.key);
  if (!col || col.sortable === false) return rows;
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = col.value(a);
    const vb = col.value(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
};

// Drag-to-resize grip on the right edge of each header cell.
const startResize = (e, key, setWidths) => {
  e.preventDefault();
  e.stopPropagation();
  const th = e.currentTarget.closest("th");
  const startX = e.clientX;
  const startW = th.offsetWidth;
  const move = (ev) =>
    setWidths((w) => ({ ...w, [key]: Math.max(60, startW + ev.clientX - startX) }));
  const up = () => {
    document.removeEventListener("mousemove", move);
    document.removeEventListener("mouseup", up);
  };
  document.addEventListener("mousemove", move);
  document.addEventListener("mouseup", up);
};

const HeaderCells = ({ cols, sort, onSort, setWidths, dense }) =>
  cols.map((col) => (
    <th
      key={col.key}
      scope="col"
      className={ClassNames(
        "bali_hai py-3 text-left text-sm font-medium select-none relative",
        dense ? "px-4" : "px-6"
      )}
    >
      <span
        onClick={col.sortable === false ? undefined : () => onSort(col)}
        className={col.sortable === false ? "" : "cursor-pointer hover:text-gray-700"}
      >
        {col.label}
        {sort?.key === col.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
      </span>
      {col.info && (
        <span
          title={col.info}
          className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full border border-gray-300 text-gray-400 text-xs cursor-help align-middle"
        >
          i
        </span>
      )}
      <span
        onMouseDown={(e) => startResize(e, col.key, setWidths)}
        className="absolute inset-y-0 right-0 cursor-col-resize hover:bg-gray-300"
        style={{ width: "6px" }}
      />
    </th>
  ));

// ---- User-agent summary (the Device / Browser column) ----
//
// requestUserAgent is already persisted on every touch — the 08-24 attribution
// analysis was run off it — but the dashboard never surfaced it. This is a
// deliberately small heuristic, NOT a UA-parsing library: it only has to
// separate the populations we act on (iOS vs Android vs desktop, Safari vs
// Chrome), and a wrong guess costs a label, never data.
//
// Order matters in both lists. Every iOS browser and every in-app webview
// impersonates Safari, and Edge/Opera/Samsung impersonate Chrome, so the most
// specific token has to be tested first.
//
// Two known blind spots, both by design:
//   * Brave is indistinguishable from Chrome here — it mimics Chrome's UA on
//     purpose. It matters because Brave strips fbclid, so a "Chrome" row can
//     really be Brave losing the click id.
//   * iPadOS reports itself as Macintosh and is only separable by touch
//     points, which we do not have server-side. A modern iPad reads as "Mac".
//     This is exactly why discount gating should key on CHANNEL, not device.
const parseUA = (ua) => {
  if (!ua) return null;
  const s = String(ua);
  const has = (re) => re.test(s);

  let device = "Other";
  if (has(/iPhone/i)) device = "iPhone";
  else if (has(/iPad/i)) device = "iPad";
  else if (has(/Android/i)) device = has(/Mobile/i) ? "Android" : "Android tablet";
  else if (has(/Macintosh|Mac OS X/i)) device = "Mac";
  else if (has(/Windows/i)) device = "Windows";
  else if (has(/Linux|X11/i)) device = "Linux";

  let browser = "Other";
  if (has(/FBAN|FBAV|FB_IAB/i)) browser = "Facebook app";
  else if (has(/Instagram/i)) browser = "Instagram app";
  else if (has(/EdgA?\//i)) browser = "Edge";
  else if (has(/OPR\/|Opera/i)) browser = "Opera";
  else if (has(/SamsungBrowser/i)) browser = "Samsung";
  else if (has(/CriOS/i)) browser = "Chrome";
  else if (has(/FxiOS/i)) browser = "Firefox";
  else if (has(/Firefox/i)) browser = "Firefox";
  else if (has(/Chrome|Chromium/i)) browser = "Chrome";
  else if (has(/Safari/i)) browser = "Safari";

  return { device, browser };
};

// Where a row's traffic came from, read off the FIRST touch (the visit that
// set the channel this row is filed under).
//
// Everything here is already captured and stored per touch — referrer,
// landingPath, utm_*, and the uncategorized `extra` params — it was just
// buried in the expandable Params view. The Direct tab surfaces it as a
// column because that tab's whole question is "what IS this traffic?".
//
// Returns null only when the row has NO touch at all, which is a different
// fact from "direct": it means there was no landing-page visit to attribute
// (an app-side signup), not a visit that arrived without a referrer.
const sourceInfo = (row) => {
  const t = row.firstTouch || row.lastTouch;
  if (!t) return null;
  let host = "";
  try { host = new URL(t.referrer).hostname.replace(/^www\./, ""); } catch { /* no/invalid referrer */ }
  // Params beyond the named utm_*/click-id set (ttclid, msclkid, ref, ...) —
  // often the only clue on an otherwise untagged link.
  const extra = Object.entries(t.extra || {}).map(([k, v]) => `${k}=${v}`);
  return {
    channel: t.channel || "direct",
    host,
    referrer: t.referrer || "",
    landingPath: t.landingPath || "",
    utmSource: t.utmSource || "",
    utmMedium: t.utmMedium || "",
    extra,
  };
};

// The FIRST touch's user agent: that is the device that actually carried the
// ad click id, and it matches how the tabs bucket rows (by initial touch).
const uaOf = (r) => r.firstTouch?.requestUserAgent ?? r.lastTouch?.requestUserAgent ?? null;

// First-touch device plus the last-touch device when the person moved between
// them — the desktop-researches-then-signs-up-on-phone hop. `moved` is false
// when there is only one touch, or when both touches are the same device and
// browser, so an unchanged journey renders as a single line.
//
// CAVEAT: this can only ever show devices that produced a TOUCH. An app signup
// that carries no attribution params is dropped by sanitizeTouch (user.ts —
// requestUserAgent is not in its hasSignal list), so the web-to-app hop is
// invisible here today. See the label below.
const uaJourney = (r) => {
  const first = parseUA(r.firstTouch?.requestUserAgent);
  const last = parseUA(r.lastTouch?.requestUserAgent);
  if (!first && !last) return null;
  const a = first ?? last;
  const moved = Boolean(first && last &&
    (first.device !== last.device || first.browser !== last.browser));
  return { first: a, last: moved ? last : null, moved };
};

// Wraps a table (or panel) so an in-flight request is visible without the
// layout collapsing.
//
// Every query on this page uses keepPreviousData, which is what stops the table
// flashing empty on every tab switch — but it also means isLoading is false
// from the second fetch onward, so the old `{isLoading && <Spinner/>}` never
// fired again after the first paint. Switching tab or moving the date range
// looked like nothing was happening while the stale numbers sat there.
//
// Dimming alone carries it: the rows stay readable, they visibly are not the
// answer yet, and pointer-events-none stops a click landing on a row that is
// about to be replaced.
//
// Dimming says nothing when there is nothing to dim, though — an empty table
// mid-fetch is indistinguishable from an empty result. `empty` adds an explicit
// "Loading…" for exactly that case, and only that case: showing it while stale
// rows are on screen would compete with the numbers the admin is still reading.
const Busy = ({ busy, empty = false, children }) => (
  <div className={ClassNames("transition-opacity", busy && "opacity-90 pointer-events-none")}>
    {busy && empty && (
      <div className="text-xs bali_hai pb-2" role="status" aria-live="polite">
        Loading…
      </div>
    )}
    {children}
  </div>
);

const Colgroup = ({ cols, widths }) => (
  <colgroup>
    {cols.map((c) => (
      <col key={c.key} style={widths[c.key] ? { width: `${widths[c.key]}px` } : undefined} />
    ))}
  </colgroup>
);

// First-party marketing funnel: landing-page pre-registrations (with their
// ad touches) joined to registered users and paid visits. This data never
// comes from ad platforms — it is the internal source of truth. Top section
// is the all-channel aggregate; the tabs below list individual
// pre-registrations bucketed by the INITIAL referrer's channel.
const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// The picker works in the viewer's local dates, the database stores UTC
// instants. Sending a bare "2026-08-11" made the backend read it as UTC
// midnight-to-midnight, so anything after ~5pm Pacific landed in the NEXT UTC
// day and vanished from "today" — roughly a third of each day's conversions.
// Convert the local day to explicit instants instead.
const localDayStartISO = (ymd) => {
  if (!ymd) return undefined;
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};

const localDayEndISO = (ymd) => {
  if (!ymd) return undefined;
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

// One switch row of the Postbacks panel: platform name, enabled toggle,
// and its configuration state. The toggle gates SENDS only — eligibility
// keeps accruing while paused, so re-enabling delivers the backlog.
const PlatformSwitch = ({ label, enabled, configured, configHint, busy, onToggle }) => (
  <label className="flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap">
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={busy}
      onClick={onToggle}
      className={ClassNames(
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none",
        enabled ? "bg-blue-chill" : "bg-gray-300"
      )}
      style={enabled ? { backgroundColor: "#37828B" } : undefined}
    >
      <span
        className={ClassNames(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          enabled ? "translate-x-4" : "translate-x-1"
        )}
      />
    </button>
    <span className="font-medium text-black">{label}</span>
    {!configured && (
      <span className="bali_hai" title={configHint}>
        (not configured)
      </span>
    )}
  </label>
);

// Postback controls: pause/resume each platform, run the job on demand, and
// see whether the nightly job is alive. Sits under the summary table so the
// numbers above stay the page's focus.
const PostbacksPanel = ({ postbacks }) => {
  const { controls, isLoading, setControls, run } = postbacks;
  if (isLoading || !controls) return null;

  const lastRun = controls.lastRun;
  const runResult = run.data;
  const fmtWhen = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const sendNow = () => {
    if (!window.confirm(
      "Send postbacks now? Eligible conversions go to the enabled platforms " +
      "(neutral campaigns only) and are stamped as delivered. Dry run previews instead."
    )) return;
    run.mutate(false);
  };

  return (
    <div className="mt-4 rounded-md border border-gray-300 shadow-sm bg-white p-4 flex flex-col gap-3">
      <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
        <span className="text-xs font-semibold text-black">Postbacks</span>
        <PlatformSwitch
          label="Auto (on payment)"
          enabled={controls.autoEnabled}
          configured={controls.googleApiConfigured || controls.capiConfigured}
          configHint="No platform configured in this environment — manual runs only"
          busy={setControls.isLoading}
          onToggle={() => setControls.mutate({ autoEnabled: !controls.autoEnabled })}
        />
        <PlatformSwitch
          label="Meta CAPI"
          enabled={controls.metaEnabled}
          configured={controls.capiConfigured}
          configHint="FB_PIXEL_ID / FB_CAPI_TOKEN missing in this environment"
          busy={setControls.isLoading}
          onToggle={() => setControls.mutate({ metaEnabled: !controls.metaEnabled })}
        />
        <PlatformSwitch
          label="Google"
          enabled={controls.googleEnabled}
          configured={controls.googleApiConfigured}
          configHint="GOOGLE_DM_* vars missing in this environment (absent on staging by design)"
          busy={setControls.isLoading}
          onToggle={() => setControls.mutate({ googleEnabled: !controls.googleEnabled })}
        />
        <div className="flex items-center gap-2 ml-auto">
          {/* Sends are batch-level, so the action lives here (on the current
              backlog), not on historical rows. Nothing pending = no button
              to press, per Omer. */}
          <span className="text-xs bali_hai whitespace-nowrap">
            Pending: Meta {controls.pendingMeta ?? 0} · Google {controls.pendingGoogle ?? 0}
          </span>
          <button
            type="button"
            disabled={run.isLoading}
            onClick={() => run.mutate(true)}
            className="h-8 px-3 rounded-md border border-gray-300 shadow-sm bg-white text-xs font-medium light_grayish_navy hover:bg-gray-50 disabled:opacity-50"
          >
            Dry run
          </button>
          {(controls.pendingMeta ?? 0) + (controls.pendingGoogle ?? 0) > 0 && (
            <button
              type="button"
              disabled={run.isLoading}
              onClick={sendNow}
              className="h-8 px-3 rounded-md text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "#37828B" }}
            >
              {run.isLoading ? "Running..." : `Send ${(controls.pendingMeta ?? 0) + (controls.pendingGoogle ?? 0)} now`}
            </button>
          )}
        </div>
      </div>
      {/* Environment-level warning: with a test code set, every Meta send is
          accepted into a test bucket and reaches neither reporting nor
          optimization. Expected on staging; on production it means the env
          block is wrong and no conversion is really being reported. */}
      {controls.capiTestMode && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Meta CAPI is in <span className="font-medium">test mode</span> (FB_CAPI_TEST_CODE set) — sends
          land in Events Manager's Test events and are never reported or optimized against.
        </div>
      )}
      <div className="text-xs bali_hai">
        {controls.autoEnabled ? (
          <>Conversions post <span className="text-green-700 font-medium">on payment</span> (no schedule, nothing batched). </>
        ) : (
          <>Auto-postback <span className="text-amber-600 font-medium">off</span> — conversions wait for a manual send. </>
        )}
        {lastRun ? (
          <>
            Last run {fmtWhen(lastRun.at)} ({lastRun.trigger}
            {lastRun.dryRun ? ", dry run" : ""}) — Meta {lastRun.metaSent}/{lastRun.metaEligible} sent
            {lastRun.metaError ? <span className="text-red-500"> · {lastRun.metaError}</span> : ""}
            {" · "}Google {lastRun.googleSent}/{lastRun.googleEligible} sent
            {lastRun.googleError ? <span className="text-red-500"> · {lastRun.googleError}</span> : ""}
          </>
        ) : (
          "Never run in this environment."
        )}
      </div>
      {runResult && (
        <div className="text-xs text-black bg-gray-50 border border-gray-200 rounded-md p-2">
          {runResult.dryRun ? "Dry run: " : "Sent: "}
          Meta {runResult.dryRun ? `${runResult.metaEligible.length} eligible` : `${runResult.metaSent} of ${runResult.metaEligible.length}`}
          {runResult.metaSkipped ? ` (${runResult.metaSkipped})` : ""}
          {" · "}
          Google {runResult.dryRun ? `${runResult.googleRows.length} rows` : `${runResult.googleSent} of ${runResult.googleRows.length}`}
          {runResult.googleSkipped ? ` (${runResult.googleSkipped})` : ""}
          {runResult.metaError ? <span className="text-red-500"> · Meta: {runResult.metaError}</span> : ""}
          {runResult.googleError ? <span className="text-red-500"> · Google: {runResult.googleError}</span> : ""}
        </div>
      )}
    </div>
  );
};

// One-off data repair: between the app-attribution deploy (2026-08-24) and
// the backend sanitizeTouch fix, Android app opens stored the Play referrer's
// "(not set)" placeholder as a real touch, misfiling those conversions under
// (direct / unknown) in the campaign grouping. Preview lists every affected
// record with its before/after campaign; Repair pulls the junk touches so the
// original landing touch is last again. Postbacks never read the junk, so
// this changes the dashboard only. The panel announces itself only when there
// is something to repair — after a clean run it reports the result and then
// disappears for good.
// Lapsed-registration 10%-off follow-up: patients who created an account and
// never opened a visit get an email (+ push pointing at it) with the discount.
// Same control shape as PostbacksPanel — a switch that gates the recurring
// sweep only, a dry-run Preview that lists exactly who would be mailed, and a
// Send now that always works regardless of the switch.
const FollowUpPanel = ({ followUp }) => {
  const { controls, isLoading, setControls, run } = followUp;
  if (isLoading || !controls) return null;
  const result = run.data;

  const sendNow = () => {
    if (!window.confirm(
      "Send the 10%-off follow-up now? Every registered patient with no visit " +
      `(${FOLLOWUP_DELAY_LABEL} after signup, outside their local quiet hours) gets the ` +
      "email and push, and the discount is granted on their account. Preview shows the list instead."
    )) return;
    run.mutate(false);
  };

  const fmtWhen = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-4 rounded-md border border-gray-300 shadow-sm bg-white p-4 flex flex-col gap-3">
      <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
        <span className="text-xs font-semibold text-black">10% follow-up</span>
        <span className="text-xs bali_hai">
          Registered {controls.delayHours}h+ ago, no visit started → email + push with the discount
        </span>
        <PlatformSwitch
          label="Auto (recurring)"
          enabled={controls.autoEnabled}
          configured={Boolean(controls.schedule)}
          configHint="No FOLLOWUP_OFFER_CRON in this environment — manual runs only, toggle has no effect"
          busy={setControls.isLoading}
          onToggle={() => setControls.mutate({ autoEnabled: !controls.autoEnabled })}
        />
        {!controls.templateConfigured && (
          <span className="text-xs text-red-600 font-medium" title="TemplateIds.PATIENT_FOLLOWUP_DISCOUNT is still the placeholder — live runs refuse to send until the real SendGrid template id is deployed.">
            email template not configured
          </span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            disabled={run.isLoading}
            onClick={() => run.mutate(true)}
            className="h-8 px-3 rounded-md border border-gray-300 shadow-sm bg-white text-xs font-medium light_grayish_navy hover:bg-gray-50 disabled:opacity-50"
          >
            Preview
          </button>
          <button
            type="button"
            disabled={run.isLoading || !controls.templateConfigured}
            onClick={sendNow}
            className="h-8 px-3 rounded-md text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: "#37828B" }}
          >
            {run.isLoading ? "Running..." : "Send now"}
          </button>
        </div>
      </div>
      {controls.lastRun && (
        <div className="text-xs bali_hai">
          Last run {fmtWhen(controls.lastRun.at)} ({controls.lastRun.trigger}
          {controls.lastRun.dryRun ? ", preview" : ""}): {controls.lastRun.candidates} candidate{controls.lastRun.candidates === 1 ? "" : "s"}
          {controls.lastRun.dryRun ? "" : `, ${controls.lastRun.sent} sent`}
          {controls.lastRun.skippedQuietHours > 0 && `, ${controls.lastRun.skippedQuietHours} held for quiet hours`}
          {controls.lastRun.failed > 0 && <span className="text-red-500">, {controls.lastRun.failed} failed</span>}
          {controls.lastRun.error && <span className="text-red-500"> — {controls.lastRun.error}</span>}
        </div>
      )}
      {run.isError && <div className="text-xs text-red-500">{errorFormatter(run.error)}</div>}
      {result && (
        <div className="text-xs text-black bg-gray-50 border border-gray-200 rounded-md p-2 flex flex-col gap-1">
          {result.dryRun ? (
            result.candidates === 0
              ? "Preview: nobody to nudge — every registered patient has started a visit or was already sent the offer."
              : `Preview: ${result.candidates} patient(s) would get the offer` +
                (result.skippedQuietHours > 0 ? ` (${result.skippedQuietHours} currently in quiet hours would wait for the next run)` : "") + "."
          ) : (
            `Sent to ${result.sent} patient(s)` +
            (result.skippedQuietHours > 0 ? `, ${result.skippedQuietHours} held for quiet hours` : "") +
            (result.failed > 0 ? `, ${result.failed} failed` : "") + "."
          )}
          {result.dryRun && result.preview?.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left bali_hai">
                    <th className="pr-4 font-medium">Email</th>
                    <th className="pr-4 font-medium">Registered</th>
                    <th className="pr-4 font-medium">State</th>
                    <th className="pr-4 font-medium">Offer</th>
                  </tr>
                </thead>
                <tbody>
                  {result.preview.map((r) => (
                    <tr key={r.username}>
                      <td className="pr-4">{r.username}</td>
                      <td className="pr-4 whitespace-nowrap">{r.registeredAt ? fmtWhen(r.registeredAt) : "—"}</td>
                      <td className="pr-4">{r.stateName ?? "—"}</td>
                      {/* A row that already carries the landing-funnel offer is
                          reminded of it, not granted a second one. */}
                      <td className="pr-4">{r.alreadyOffered ? "reminder (already offered)" : "new grant"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.candidates > result.preview.length && (
                <div className="bali_hai mt-1">…and {result.candidates - result.preview.length} more.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TouchRepairPanel = ({ repair }) => {
  const result = repair.data;
  const records = result?.records ?? [];

  const runRepair = () => {
    if (!window.confirm(
      `Remove the "(not set)" placeholder touches from ${result.affected} record(s)? ` +
      "Their conversions re-file under the campaign of their real landing touch. " +
      "This cannot be undone, but removes ONLY placeholder touches — real ones are untouched."
    )) return;
    repair.mutate(false);
  };

  return (
    <div className="mt-4 rounded-md border border-gray-300 shadow-sm bg-white p-4 flex flex-col gap-3">
      <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
        <span className="text-xs font-semibold text-black">Touch repair</span>
        <span className="text-xs bali_hai">
          Removes "(not set)" Play-referrer placeholders stored 8/24–8/26 that misfile
          conversions as (direct / unknown).
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            disabled={repair.isLoading}
            onClick={() => repair.mutate(true)}
            className="h-8 px-3 rounded-md border border-gray-300 shadow-sm bg-white text-xs font-medium light_grayish_navy hover:bg-gray-50 disabled:opacity-50"
          >
            Preview
          </button>
          {result?.dryRun && result.affected > 0 && (
            <button
              type="button"
              disabled={repair.isLoading}
              onClick={runRepair}
              className="h-8 px-3 rounded-md text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "#37828B" }}
            >
              {repair.isLoading ? "Repairing..." : `Repair ${result.affected} record(s)`}
            </button>
          )}
        </div>
      </div>
      {repair.isError && (
        <div className="text-xs text-red-500">{errorFormatter(repair.error)}</div>
      )}
      {result && (
        <div className="text-xs text-black bg-gray-50 border border-gray-200 rounded-md p-2 flex flex-col gap-1">
          {result.dryRun ? (
            result.affected === 0
              ? "Preview: nothing to repair — no \"(not set)\" touches found."
              : `Preview: ${result.affected} record(s) would be repaired.`
          ) : (
            `Repaired ${result.modified} record(s). The funnel above already reflects the fix.`
          )}
          {records.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left bali_hai">
                    <th className="pr-4 font-medium">Email</th>
                    <th className="pr-4 font-medium">Pre-registered</th>
                    <th className="pr-4 font-medium">Files under</th>
                    <th className="pr-4 font-medium">{result.dryRun ? "Will file under" : "Now files under"}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.username}>
                      <td className="pr-4">{r.username}</td>
                      <td className="pr-4 whitespace-nowrap">{new Date(r.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="pr-4">{result.dryRun ? (r.currentCampaign ?? "(direct / unknown)") : (r.repairedCampaign ?? "(direct / unknown)")}</td>
                      <td className="pr-4">{r.repairedCampaign ?? "(direct / unknown)"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Ledger columns for the Postbacks tab. Server-sorted (newest paid visit
// first) and paginated; header sorting is disabled on purpose.
const LOG_COLS = [
  { key: "username", label: "Email", sortable: false },
  { key: "channel", label: "Channel", sortable: false },
  { key: "utmCampaign", label: "Campaign", sortable: false },
  { key: "firstPaidAt", label: "Paid at", sortable: false },
  { key: "value", label: "Revenue", sortable: false },
  { key: "meta", label: "Meta", sortable: false, info: "What was (or will be) sent to Meta: click id, event time, value only — never an email." },
  { key: "google", label: "Google", sortable: false, info: "Sheet row for Google Ads' daily import: gclid, conversion name, time, value." },
];

// Run history: one row per postback run. Failures are batch-level (one CAPI
// request / one sheet append per run), so this table IS the failure log; a
// failed batch is retried by the next run's row.
const RUN_COLS = [
  { key: "at", label: "Run at", sortable: false },
  { key: "trigger", label: "Trigger", sortable: false },
  { key: "meta", label: "Meta", sortable: false, info: "eligible → sent in this run; the platform's error appears here when its send failed" },
  { key: "google", label: "Google", sortable: false, info: "eligible → sheet rows appended in this run; the platform's error appears here when its append failed" },
  { key: "outcome", label: "Outcome", sortable: false },
];

// "success" is reserved for runs that actually reached a platform. A run sent
// with FB_CAPI_TEST_CODE is accepted by Meta and counted, but never reaches
// reporting or optimization — calling that success is how a staging send
// passes for a production delivery, so it gets its own amber label.
const runOutcome = (r) => {
  if (r.dryRun) return { cls: "bg-gray-100 text-gray-500", label: "preview" };
  if (r.metaError || r.googleError) return { cls: "bg-red-100 text-red-700", label: "failed" };
  if (r.metaTestMode && r.metaSent > 0)
    return { cls: "bg-amber-100 text-amber-700", label: "test mode" };
  return { cls: "bg-green-100 text-green-700", label: "success" };
};

// Platform cell: counts, plus that platform's own error underneath when its
// send failed — so a mixed run (Meta ok, Google failed) reads unambiguously.
const RunPlatformCell = ({ eligible, sent, error, testMode, warnings, held, skipped }) => (
  <td className="px-4 py-2 text-sm light_grayish_navy align-top">
    {eligible} → {sent}
    {error && (
      <div className="text-xs text-red-500 max-w-[16rem] truncate" title={error}>
        {error}
      </div>
    )}
    {/* WHY THIS LINE EXISTS. "0 → 0" was ambiguous in the worst possible way:
        a night that withheld every conversion looked exactly like a night with
        no traffic. That is what made the Sep 2 run unreadable — three real
        conversions were held for click age and the row said nothing happened.
        Runs recorded before the backend started persisting these counts have
        no held fields, so nothing renders for them. */}
    {!error && held?.length > 0 && (
      <div className="text-xs text-amber-600">{held.join(" · ")}</div>
    )}
    {/* Paused in the admin, as opposed to having nothing to send. */}
    {skipped && <div className="text-xs text-amber-600">{skipped}</div>}
    {/* Accepted into a test bucket, not delivered — say so where the count is,
        not only in the Outcome badge. */}
    {!error && testMode && sent > 0 && (
      <div className="text-xs text-amber-600">test events only — not reported</div>
    )}
    {/* Meta's soft failures: a 200 can still carry complaints. */}
    {warnings?.length > 0 && (
      <div className="text-xs text-amber-600 max-w-[16rem] truncate" title={warnings.join("\n")}>
        {warnings.length} warning{warnings.length > 1 ? "s" : ""} from platform
      </div>
    )}
  </td>
);

const PostbackRunsTable = ({ runs, page, setPage, run }) => {
  const totalPages = Math.max(1, Math.ceil(runs.total / PER_PAGE));
  // Retry only makes sense on the most recent LIVE run: older failed batches
  // dissolved back into the pending pool, so "retrying" them IS running the
  // current backlog — which is what this button does.
  const firstLiveIdx = page === 1 ? runs.rows.findIndex((r) => !r.dryRun) : -1;
  return (
    <div className="mt-4">
      <Table
        heading={<HeaderCells cols={RUN_COLS} sort={null} onSort={() => {}} setWidths={() => {}} dense />}
        body={
          !runs.isLoading && runs.rows.length > 0 ? (
            runs.rows.map((r, i) => {
              const o = runOutcome(r);
              const retryable = i === firstLiveIdx && (r.metaError || r.googleError);
              return (
                <tr key={`${r.at}-${i}`} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-sm text-black whitespace-nowrap">
                    {new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-2 text-sm light_grayish_navy align-top">{r.trigger}{r.dryRun ? " (dry)" : ""}</td>
                  <RunPlatformCell
                    eligible={r.metaEligible}
                    sent={r.metaSent}
                    error={r.metaError}
                    testMode={r.metaTestMode}
                    warnings={r.metaWarnings}
                    skipped={r.metaSkipped}
                  />
                  <RunPlatformCell
                    eligible={r.googleEligible}
                    sent={r.googleSent}
                    error={r.googleError}
                    skipped={r.googleSkipped}
                    held={[
                      r.googleHeldForClickAge > 0 && `${r.googleHeldForClickAge} held — click too recent`,
                      r.googleHeldForBraidColumns > 0 && `${r.googleHeldForBraidColumns} held — iOS, no sheet column`,
                    ].filter(Boolean)}
                  />
                  <td className="px-4 py-2 whitespace-nowrap align-top">
                    <span className={ClassNames("inline-block px-1.5 py-0.5 rounded-full text-xs font-medium", o.cls)}>{o.label}</span>
                    {retryable ? (
                      <button
                        type="button"
                        disabled={run.isLoading}
                        onClick={() => run.mutate(false)}
                        className="ml-2 px-2 py-0.5 rounded-md text-xs font-medium text-white disabled:opacity-50"
                        style={{ backgroundColor: "#37828B" }}
                      >
                        {run.isLoading ? "Retrying..." : "Retry"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })
          ) : (
            !runs.isLoading && "No runs recorded yet"
          )
        }
      />
      {runs.isLoading && <Spinner />}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm bali_hai">
          Page {Math.min(page, totalPages)} of {totalPages} · {runs.total} run{runs.total === 1 ? "" : "s"}
        </div>
        <div className="w-56">
          <PaginationBtns
            onPrevious={() => page > 1 && setPage(page - 1)}
            onNext={() => page < totalPages && setPage(page + 1)}
            isPrevDisabled={page <= 1}
            isNextDisabled={page >= totalPages}
          />
        </div>
      </div>
    </div>
  );
};

const PostbacksLedger = ({ log, page, setPage }) => {
  const totalPages = Math.max(1, Math.ceil(log.total / PER_PAGE));
  return (
    <div className="mt-4">
      <Table
        heading={<HeaderCells cols={LOG_COLS} sort={null} onSort={() => {}} setWidths={() => {}} dense />}
        body={
          !log.isLoading && log.rows.length > 0 ? (
            log.rows.map((r) => (
              <tr key={`${r.username}-${r.firstPaidAt}`} className="border-t border-gray-100">
                <td className="px-4 py-2 text-sm text-black whitespace-nowrap">{r.username}</td>
                <td className="px-4 py-2 text-sm light_grayish_navy capitalize">{r.channel || "—"}</td>
                <td className="px-4 py-2 text-sm light_grayish_navy">{r.utmCampaign || "—"}</td>
                <td className="px-4 py-2 text-sm light_grayish_navy whitespace-nowrap">
                  {r.firstPaidAt ? new Date(r.firstPaidAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </td>
                <td className="px-4 py-2 text-sm text-black">${Number(r.value ?? 0).toFixed(2)}</td>
                <td className="px-4 py-2"><DeliveryChip label="Meta" p={r.meta} judged={r} /></td>
                <td className="px-4 py-2"><DeliveryChip label="Google" p={r.google} judged={r} /></td>
              </tr>
            ))
          ) : (
            !log.isLoading && "No conversions recorded yet"
          )
        }
      />
      {log.isLoading && <Spinner />}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm bali_hai">
          Page {Math.min(page, totalPages)} of {totalPages} · {log.total} conversion{log.total === 1 ? "" : "s"}
        </div>
        <div className="w-56">
          <PaginationBtns
            onPrevious={() => page > 1 && setPage(page - 1)}
            onNext={() => page < totalPages && setPage(page + 1)}
            isPrevDisabled={page <= 1}
            isNextDisabled={page >= totalPages}
          />
        </div>
      </div>
    </div>
  );
};

// Filters survive a refresh (tab, date range, grouping). Stored as one blob
// so clearing is a single key; malformed storage falls back to defaults.
const FILTERS_KEY = "CONVERSIONS_FILTERS";
const savedFilters = () => {
  try { return JSON.parse(localStorage.getItem(FILTERS_KEY)) || {}; } catch { return {}; }
};

export function ConversionsMainPage() {
  const [from, setFrom] = useState(() => savedFilters().from || todayYMD());
  const [to, setTo] = useState(() => savedFilters().to || todayYMD());
  const [groupBy, setGroupBy] = useState(() => savedFilters().groupBy || "channel");
  const [apiError, setApiError] = useState("");
  const [tab, setTab] = useState(() => savedFilters().tab || "all");
  const [page, setPage] = useState(1);
  const [previewImg, setPreviewImg] = useState(null);
  const [hoverImg, setHoverImg] = useState(null);
  const [summarySort, setSummarySort] = useState(null);
  const [detailSort, setDetailSort] = useState(null);
  // Selected campaign chip (null = the whole tab). Deliberately not persisted
  // with the other filters: it only makes sense against the campaign strip
  // that is on screen, and a stale one would silently empty the table on the
  // next visit.
  const [campaign, setCampaign] = useState(null);
  const [summaryWidths, setSummaryWidths] = useState({});
  const [detailWidths, setDetailWidths] = useState({});

  // Persist on every filter change; storage-blocked browsers just lose
  // persistence, never the page.
  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify({ tab, from, to, groupBy })); } catch { /* noop */ }
  }, [tab, from, to, groupBy]);

  const toggleSort = (setter) => (col) =>
    setter((s) =>
      s?.key === col.key
        ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: col.key, dir: col.numeric ? "desc" : "asc" }
    );

  // Local calendar day -> UTC instants, so an evening conversion still counts
  // as "today" for the person reading the dashboard.
  const fromInstant = localDayStartISO(from);
  const toInstant = localDayEndISO(to);

  // The Attribution tab renders ONLY <AttributionPanel/>, which needs nothing
  // but its own waterfall call — so the summary, spend and list queries are all
  // switched off there. This is not a micro-optimisation: opening the tab used
  // to fire five heavy aggregations at once, and with axios capped at 50s
  // (AxiosInterceptors.js) the slowest of them, the waterfall itself, was the
  // one that got cancelled. The panel then retried and spun forever.
  //
  // The list query was the worst offender. getMarketingConversionsList maps an
  // UNKNOWN tab name to "no channel filter", so tab=attribution silently
  // requested the unfiltered full set — the most expensive variant there is —
  // to build a response this tab throws away.
  // Traffic is the same story: the tab renders ONLY <TrafficPanel/>, which
  // makes its own single call. Leaving the funnel queries on would fire the
  // same five aggregations for a response the tab never reads — and the list
  // query maps an unknown tab name to "no channel filter", i.e. the most
  // expensive variant there is.
  const onAttribution = tab === "attribution";
  const onTraffic = tab === "traffic";
  const noFunnel = onAttribution || onTraffic;
  // Conversions funnel + spend in a single request off one shared funnel pass
  // (was useConversions + useSpend = two calls, three backend funnel scans). The
  // merged dashboard also carries the landing-impression columns. The per-tab
  // campaign filter below still uses a channel-scoped useConversions.
  // Only the All Channels + per-channel tabs render dashboard data (summary +
  // spend). Postbacks/Attribution/Traffic render none of it, so the heavy
  // funnel fold is skipped there.
  const { rows, totals, spend, isLoading, isFetching, isError, error } = useMarketingDashboard({
    from: fromInstant,
    to: toInstant,
    groupBy,
    enabled: !noFunnel && tab !== "postbacks",
  });
  const list = useConversionsList({
    tab,
    page,
    perPage: PER_PAGE,
    from: fromInstant,
    to: toInstant,
    campaign,
    enabled: !noFunnel && tab !== "all" && tab !== "postbacks",
  });
  // Per-campaign funnel for the channel tabs. Same aggregation as the All
  // Channels summary, scoped server-side to this tab's channel and always
  // grouped by campaign (the grouping selector only applies to All Channels).
  // Spend comes from the merged dashboard call above.
  //
  // Fetched for EVERY channel tab, not just the ones with a campaign filter.
  // It feeds two things now: the filter chips (paid tabs only) and the summary
  // table above the detail rows (all tabs). Before, a channel tab showed only
  // the pre-registration list — so the 148 Google impressions visible on All
  // Channels vanished the moment you opened the Google tab, and the tab looked
  // like it had captured nothing but a handful of registrations.
  const showCampaignFilter = CAMPAIGN_FILTER_TABS.includes(tab);
  const campaignFunnel = useConversions({
    from: fromInstant,
    to: toInstant,
    groupBy: "campaign",
    tab,
    enabled: !noFunnel && tab !== "all" && tab !== "postbacks",
  });
  // The quiz behind the impressions, per campaign, for the channel tabs'
  // cards: who answered what, who was turned away and why.
  const quiz = useQuizFunnel({
    from: fromInstant,
    to: toInstant,
    groupBy: "campaign",
    tab,
    enabled: !noFunnel && tab !== "all" && tab !== "postbacks",
  });
  const postbacks = usePostbackControls();
  const touchRepair = useTouchRepair();
  // Follow-up controls only render in the Postbacks tab's FollowUpPanel, so the
  // controls fetch is scoped to that tab instead of firing on every tab.
  const followUp = useFollowUpOffer({ enabled: tab === "postbacks" });
  const [showLedger, setShowLedger] = useState(false);
  const postbackRuns = usePostbackRuns({ page, perPage: PER_PAGE, enabled: tab === "postbacks" });
  const postbackLog = usePostbackLog({ page: 1, perPage: PER_PAGE, enabled: tab === "postbacks" && showLedger });

  if ((isError || list.isError) && !apiError) {
    setApiError(
      errorFormatter(
        (isError ? error : list.error)?.response?.data?.errors ||
          (isError ? error : list.error)?.message
      )
    );
  }

  const totalPages = Math.max(1, Math.ceil(list.total / PER_PAGE));
  const summaryCols = [
    ...SUMMARY_COLS,
    ...(spend.configured ? SPEND_COLS : []),
    ...(groupBy === "channel" ? [SUMMARY_POSTBACK_COL] : []),
  ];
  // Merge Meta spend onto the funnel rows the summary table is grouped by.
  // Only campaign (utm_campaign) and content (utm_content) groupings have
  // join keys; two Meta campaigns can map to one utm key, so spend sums.
  // CPA/ROAS are recomputed from the row's own funnel numbers so both
  // columns always agree with what the row displays.
  const spendSource =
    spend.configured && groupBy === "campaign" ? spend.campaigns :
    spend.configured && groupBy === "content" ? spend.ads : null;
  const spendByKey = new Map();
  for (const s of spendSource ?? []) {
    if (!s.key) continue;
    const cur = spendByKey.get(s.key) || { spend: 0 };
    cur.spend += s.spend;
    spendByKey.set(s.key, cur);
  }
  const rowsWithSpend = rows.map((r) => {
    const s = spendByKey.get(r.key);
    if (!s) return r;
    return {
      ...r,
      spend: s.spend,
      cpa: r.paidPatients ? s.spend / r.paidPatients : null,
      roas: s.spend > 0 ? (r.revenue ?? 0) / s.spend : null,
    };
  });
  const sortedSummary = sortRows(rowsWithSpend, summaryCols, summarySort);
  // The channel tabs' own summary. Always grouped by campaign, so it uses the
  // campaign column set rather than summaryCols (which may carry the channel
  // -only postback column). Spend is merged the same way, by utm_campaign.
  const tabSummaryCols = [
    ...SUMMARY_COLS,
    ...(spend.configured ? SPEND_COLS : []),
  ];
  // Its own spend map, keyed by campaign unconditionally: spendByKey above is
  // built from whichever grouping All Channels is on, which is "channel" by
  // default and would leave every tab row without spend.
  const tabSpendByKey = new Map();
  for (const s of (spend.configured ? spend.campaigns : null) ?? []) {
    if (!s.key) continue;
    const cur = tabSpendByKey.get(s.key) || { spend: 0, clicks: 0 };
    cur.spend += s.spend;
    cur.clicks += s.clicks || 0;
    tabSpendByKey.set(s.key, cur);
  }
  const tabRowsWithSpend = campaignFunnel.rows.map((r) => {
    const q = quiz.byKey.get(String(r.key ?? ""));
    const base = q ? { ...r, quiz: q } : r;
    const s = tabSpendByKey.get(r.key);
    if (!s) return base;
    return {
      ...base,
      spend: s.spend,
      // The platform's own click count for the same campaign, so the card can
      // put it beside our impressions: the two should be within tens of
      // percent of each other. Impressions far ABOVE clicks means the beacon
      // is counting loads nobody saw (in-app prefetch, prerender); far below
      // means beacons are being dropped again (2026-09-04's per-IP limiter).
      adClicks: s.clicks,
      cpa: r.paidPatients ? s.spend / r.paidPatients : null,
      roas: s.spend > 0 ? (r.revenue ?? 0) / s.spend : null,
    };
  });
  const sortedTabSummary = sortRows(tabRowsWithSpend, tabSummaryCols, summarySort);
  const detailCols = detailColsFor(tab);
  const sortedDetail = sortRows(list.rows, detailCols, detailSort);
  // Registrations grouped under their campaign's band. The funnel supplies the
  // order and the aggregate numbers; the rows are only the CURRENT PAGE of the
  // detail list, so a band can legitimately show fewer rows than its own count
  // — the band says so rather than letting the two numbers disagree silently.
  //
  // Must sit AFTER sortedDetail: reading it above its own declaration is a
  // temporal-dead-zone ReferenceError, which would blank every channel tab.
  const detailByCampaign = new Map();
  for (const r of sortedDetail) {
    const k = String(r.lastTouch?.utmCampaign ?? r.firstTouch?.utmCampaign ?? "");
    if (!detailByCampaign.has(k)) detailByCampaign.set(k, []);
    detailByCampaign.get(k).push(r);
  }
  const selectedCampaignKey =
    campaign == null ? null : campaign === CAMPAIGN_NONE ? "" : String(campaign);
  const groupedDetail = sortedTabSummary
    .filter((c) => selectedCampaignKey == null || String(c.key ?? "") === selectedCampaignKey)
    .map((c) => {
      const k = String(c.key ?? "");
      const rows = detailByCampaign.get(k) ?? [];
      detailByCampaign.delete(k);
      return { band: c, rows };
    });
  // A row whose campaign has no funnel entry must not vanish off the page.
  // Shouldn't happen — both read the same touches — but silently dropping a
  // pre-registration would be the worst possible failure for this table.
  for (const [k, rows] of detailByCampaign) {
    if (selectedCampaignKey != null && k !== selectedCampaignKey) continue;
    groupedDetail.push({
      band: { key: k, impressions: 0, preRegistered: rows.length, paidPatients: 0, revenue: 0 },
      rows,
    });
  }
  // Meta name/status for the ids on this page (facebook/instagram tabs).
  const adIds =
    tab === "facebook" || tab === "instagram"
      ? [...new Set(sortedDetail.flatMap((r) => {
          const t = r.lastTouch || {};
          return [t.campaignid, t.adgroupid, t.creative].filter(Boolean);
        }))]
      : [];
  const adInfo = useAdInfo(adIds);

  return (
    <MainLayout>
      <div className="p-8 flex flex-col gap-8">
        {/* Failure banner: latest LIVE postback run errored. Visible on every
            tab of this page; the sidebar red dot mirrors it site-wide. */}
        {postbacks.controls?.lastLiveRunFailed && (
          <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
            <span>
              Last postback run failed
              {postbacks.controls.lastLiveRun?.at
                ? ` (${new Date(postbacks.controls.lastLiveRun.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })})`
                : ""}
              {" — "}
              {[postbacks.controls.lastLiveRun?.metaError, postbacks.controls.lastLiveRun?.googleError].filter(Boolean).join(" · ")}
            </span>
            <button
              type="button"
              onClick={() => { setTab("postbacks"); setPage(1); setCampaign(null); }}
              className="ml-auto text-xs font-semibold underline whitespace-nowrap"
            >
              View postbacks
            </button>
          </div>
        )}
        {apiError && <ApiError error={apiError} setApiError={setApiError} />}
        {/* Title, channel tabs, and filters share one row (Patients-page
            pattern) so the table starts higher on screen. */}
        <div className="flex justify-between items-center flex-wrap gap-y-3">
          <div className="flex items-center gap-1.5 mr-4">
            <h1 className="font-semibold text-2xl sapphire mr-2">Conversions</h1>
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setTab(key); setPage(1); setCampaign(null); }}
                className={ClassNames(
                  "flex items-center gap-1 text-xs font-medium cursor-pointer py-1.5 px-2 rounded-md focus:outline-none whitespace-nowrap",
                  tab === key
                    ? "bg-gray-100 font-semibold text-black"
                    : "light_grayish_navy hover:bg-gray-50"
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <DateRangePicker
              from={from}
              to={to}
              onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); setPage(1); setCampaign(null); }}
            />
            {tab === "all" && (
              // Same control style as the Date range button: bordered box,
              // gray label inside, teal value.
              <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-300 shadow-sm bg-white text-xs cursor-pointer whitespace-nowrap">
                <span className="bali_hai">Group by</span>
                <select
                  className="border-none p-0 pr-7 text-xs font-medium text-blue-chill bg-white focus:outline-none focus:ring-0 cursor-pointer"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        {/* ---- One tabbed table: All Channels aggregate + per-channel detail ---- */}
        <div>
          {tab === "all" ? (
            <Busy busy={isFetching} empty={rows.length === 0}>
              <Table
                fixed={Object.keys(summaryWidths).length > 0}
                colgroup={<Colgroup cols={summaryCols} widths={summaryWidths} />}
                heading={
                  <HeaderCells
                    cols={summaryCols}
                    sort={summarySort}
                    onSort={toggleSort(setSummarySort)}
                    setWidths={setSummaryWidths}
                  />
                }
                body={
                  !isLoading && rows.length > 0 ? (
                    <SummaryBody
                      rows={sortedSummary}
                      groupBy={groupBy}
                      totals={totals}
                      showSpend={spend.configured}
                      spendTotals={spend.totals}
                      unmatched={groupBy === "campaign" ? spend.unmatched : null}
                      onSelectChannel={(key) => { setTab(key); setPage(1); setCampaign(null); }}
                    />
                  ) : (
                    !isLoading && "No Data Found"
                  )
                }
              />
            </Busy>
          ) : tab === "postbacks" ? (
            <div>
              <PostbacksPanel postbacks={postbacks} />
              <FollowUpPanel followUp={followUp} />
              <TouchRepairPanel repair={touchRepair} />
              <PostbackRunsTable runs={postbackRuns} page={page} setPage={setPage} run={postbacks.run} />
              <button
                type="button"
                onClick={() => setShowLedger((s) => !s)}
                className="mt-2 text-xs font-medium text-blue-chill hover:underline"
              >
                {showLedger ? "Hide" : "Show"} per-conversion delivery details
              </button>
              {showLedger && <PostbacksLedger log={postbackLog} page={1} setPage={() => {}} />}
            </div>
          ) : tab === "attribution" ? (
            <AttributionPanel enabled={tab === "attribution"} />
          ) : onTraffic ? (
            <TrafficPanel enabled={onTraffic} from={fromInstant} to={toInstant} />
          ) : (
            <Busy busy={list.isFetching || campaignFunnel.isFetching} empty={list.rows.length === 0 && campaignFunnel.rows.length === 0}>
              {showCampaignFilter && (
                <CampaignFilter
                  rows={campaignFunnel.rows}
                  isLoading={campaignFunnel.isLoading}
                  selected={campaign}
                  onSelect={(key) => { setCampaign(key); setPage(1); }}
                />
              )}
              {/* One card per campaign: its aggregates, then its own table.
                  Column widths are shared state across cards, so the tables
                  stay aligned with each other and a drag resizes all of them. */}
              {!list.isLoading && groupedDetail.length === 0 && "No Data Found"}
              <div className="flex flex-col gap-6">
                {groupedDetail.map((g) => (
                  <div
                    key={g.band.key ?? CAMPAIGN_NONE}
                    className="shadow bg-white"
                    // As wide as its table, never narrower: the table overflows
                    // the column on narrow screens (the page scrolls), and a
                    // container-width card would leave the grey summary band
                    // ending mid-table.
                    style={{ width: "max-content", minWidth: "100%" }}
                  >
                    <CampaignSummary row={g.band} shown={g.rows.length} />
                    {g.rows.length > 0 ? (
                      <Table
                        fixed={Object.keys(detailWidths).length > 0}
                        colgroup={<Colgroup cols={detailCols} widths={detailWidths} />}
                        heading={
                          <HeaderCells
                            cols={detailCols}
                            sort={detailSort}
                            onSort={toggleSort(setDetailSort)}
                            setWidths={setDetailWidths}
                            /* No `dense`: that switches the header to px-4
                               while the body cells are px-6, which offsets
                               every label 8px from the values beneath it. */
                          />
                        }
                        body={
                          <DetailBody
                            rows={g.rows}
                            onPreview={setPreviewImg}
                            onHover={setHoverImg}
                            adInfo={adInfo}
                            showSource={tab === "other"}
                          />
                        }
                      />
                    ) : (
                      <EmptyCampaignNote impressions={g.band.impressions ?? 0} visitors={g.band.visitors ?? 0} />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm bali_hai">
                  Page {Math.min(page, totalPages)} of {totalPages} ·{" "}
                  {list.total} pre-registration{list.total === 1 ? "" : "s"}
                  {campaign != null && (
                    <>
                      {" in "}
                      <span className="font-medium sapphire">
                        {campaign === CAMPAIGN_NONE ? "(no campaign tagged)" : campaign}
                      </span>
                      {" · "}
                      <button
                        type="button"
                        onClick={() => { setCampaign(null); setPage(1); }}
                        className="text-blue-chill hover:underline"
                      >
                        show all campaigns
                      </button>
                    </>
                  )}
                </div>
                <div className="w-56">
                  <PaginationBtns
                    onPrevious={() => page > 1 && setPage(page - 1)}
                    onNext={() => page < totalPages && setPage(page + 1)}
                    isPrevDisabled={page <= 1}
                    isNextDisabled={page >= totalPages}
                  />
                </div>
              </div>
            </Busy>
          )}
        </div>
      </div>

      {/* Hover preview: follows the cursor, never intercepts it (so leaving
          the thumbnail always fires mouseleave). Rendered here rather than in
          the cell so no table overflow can clip it. */}
      {hoverImg && (
        <div
          className="fixed z-40 pointer-events-none"
          style={{ left: hoverImg.left, top: hoverImg.top }}
        >
          <img
            src={hoverImg.src}
            alt=""
            className="rounded-lg border border-gray-200 shadow-2xl bg-white block"
            style={{ width: hoverImg.width, maxHeight: hoverImg.maxHeight }}
          />
        </div>
      )}

      {/* Image lightbox: click anywhere (or the ×) to dismiss */}
      {previewImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center cursor-zoom-out"
          style={{ backgroundColor: "rgba(16, 24, 40, 0.75)" }}
          onClick={() => setPreviewImg(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewImg(null)}
            className="absolute top-4 right-5 text-white text-3xl leading-none focus:outline-none"
            aria-label="Close preview"
          >
            ×
          </button>
          <img
            src={previewImg.src}
            alt={previewImg.alt}
            className="rounded-lg shadow-2xl"
            style={{ maxHeight: "90vh", maxWidth: "90vw" }}
          />
          <div className="absolute bottom-4 text-white text-sm opacity-80">
            {previewImg.alt}
          </div>
        </div>
      )}
    </MainLayout>
  );
}

// Bounce: of the PEOPLE who landed, the share that never gave an email.
// Clamped at zero because pre-registrations and impressions are counted at
// different moments — someone can pre-register on a visit that carried no
// query string, which was never counted as an impression — and a negative
// bounce rate is noise, not information. null when nothing was counted.
const bounceRate = (r) => {
  const people = r?.visitors ?? 0;
  if (!people) return null;
  return Math.max(0, (people - (r.preRegistered ?? 0)) / people);
};
const bouncePct = (r) => {
  const v = bounceRate(r);
  return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
};

// Spend cell formatters: em-dash when the join has no data for the row.
const money = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "—");
const ratio = (v) => (v != null ? `${Number(v).toFixed(2)}x` : "—");

const SummaryBody = ({ rows, groupBy, totals, showSpend, spendTotals, unmatched, onSelectChannel }) => (
  <>
    {rows.map((row) => {
      const convPct = row.preRegistered
        ? ((row.paidPatients / row.preRegistered) * 100).toFixed(1)
        : "0.0";
      // Channel rows always have a home tab — named ones map directly, the
      // rest fall into the "other" (Direct/Unknown) catch-all.
      const channelTab = tabForChannel(row.key);
      const label = row.key || "(direct / unknown)";
      const linkable = groupBy === "channel";
      return (
        <tr key={row.key}>
          <td className="px-6 py-4 truncate text-sm font-medium sapphire">
            {linkable ? (
              <button
                type="button"
                onClick={() => onSelectChannel(channelTab)}
                className="hover:underline cursor-pointer capitalize"
                title={`View ${label} conversions`}
              >
                {label}
              </button>
            ) : (
              label
            )}
          </td>
          <td
            className="px-6 py-4 truncate text-sm"
            title={`${row.visitors ?? 0} distinct visitor${(row.visitors ?? 0) === 1 ? "" : "s"}`}
          >
            {row.impressions ?? 0}
          </td>
          <td className="px-6 py-4 truncate text-sm">{row.preRegistered}</td>
          <td className="px-6 py-4 truncate text-sm">{bouncePct(row)}</td>
          <td className="px-6 py-4 truncate text-sm">{row.verified}</td>
          <td className="px-6 py-4 truncate text-sm">{row.registered}</td>
          <td className="px-6 py-4 truncate text-sm">{row.paidPatients}</td>
          <td className="px-6 py-4 truncate text-sm">{row.offerSent ?? 0}</td>
          <td className="px-6 py-4 truncate text-sm" title={`$${Number(row.revenueAfterOffer ?? 0).toFixed(2)} revenue after the offer`}>
            {row.paidAfterOffer ?? 0}
            {row.offerSent > 0 && (
              <span className="ml-1 text-xs bali_hai">
                ({((row.paidAfterOffer ?? 0) / row.offerSent * 100).toFixed(0)}%)
              </span>
            )}
          </td>
          <td className="px-6 py-4 truncate text-sm">
            ${Number(row.revenue ?? 0).toFixed(2)}
          </td>
          <td className="px-6 py-4 truncate text-sm">{convPct}%</td>
          {showSpend && (
            <>
              <td className="px-6 py-4 truncate text-sm">{money(row.spend)}</td>
              <td className="px-6 py-4 truncate text-sm">{money(row.cpa)}</td>
              <td className="px-6 py-4 truncate text-sm">{ratio(row.roas)}</td>
            </>
          )}
          {groupBy === "channel" && (
            <td
              className="px-6 py-4 truncate text-sm bali_hai"
              title={policyFor(row.key).tip}
            >
              {policyFor(row.key).label}
            </td>
          )}
        </tr>
      );
    })}
    {/* Meta spend that matched no funnel row (no ids on any click and no
        exact name match) — shown honestly rather than guessed onto a row. */}
    {showSpend && unmatched?.spend > 0 && (
      <tr>
        <td
          className="px-6 py-4 truncate text-sm bali_hai italic"
          title={`Meta campaigns with no matching funnel row: ${(unmatched.campaigns || []).join(", ")}`}
        >
          (unmatched Meta spend)
        </td>
        {["impressions", "preRegistered", "bounce", "verified", "registered", "paidPatients", "offerSent", "paidAfterOffer", "revenue", "conv"].map((k) => (
          <td key={k} className="px-6 py-4 truncate text-sm bali_hai">—</td>
        ))}
        <td className="px-6 py-4 truncate text-sm">{money(unmatched.spend)}</td>
        <td className="px-6 py-4 truncate text-sm bali_hai">—</td>
        <td className="px-6 py-4 truncate text-sm bali_hai">—</td>
      </tr>
    )}
    {/* Google-Ads-style totals row pinned at the bottom of the table */}
    {totals && (
      <tr className="bg-gray-50 border-t-2 border-gray-200">
        <td className="px-6 py-4 truncate text-sm font-semibold sapphire">
          Total
        </td>
        <td
          className="px-6 py-4 truncate text-sm font-semibold"
          title={`${totals.visitors ?? 0} distinct visitors (counted across the whole range, so it is NOT the sum of the rows above — one person who arrived under two campaigns is two rows and one human)`}
        >
          {totals.impressions ?? 0}
        </td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{totals.preRegistered}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{bouncePct(totals)}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{totals.verified}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{totals.registered}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{totals.paidPatients}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold">{totals.offerSent ?? 0}</td>
        <td className="px-6 py-4 truncate text-sm font-semibold" title={`$${Number(totals.revenueAfterOffer ?? 0).toFixed(2)} revenue after the offer`}>
          {totals.paidAfterOffer ?? 0}
          {totals.offerSent > 0 && (
            <span className="ml-1 text-xs bali_hai font-normal">
              ({((totals.paidAfterOffer ?? 0) / totals.offerSent * 100).toFixed(0)}%)
            </span>
          )}
        </td>
        <td className="px-6 py-4 truncate text-sm font-semibold">
          ${Number(totals.revenue ?? 0).toFixed(2)}
        </td>
        <td className="px-6 py-4 truncate text-sm font-semibold">
          {totals.preRegistered
            ? ((totals.paidPatients / totals.preRegistered) * 100).toFixed(1)
            : "0.0"}
          %
        </td>
        {showSpend && (
          <>
            <td className="px-6 py-4 truncate text-sm font-semibold">{money(spendTotals?.spend)}</td>
            <td className="px-6 py-4 truncate text-sm font-semibold">{money(spendTotals?.cpa)}</td>
            <td className="px-6 py-4 truncate text-sm font-semibold">{ratio(spendTotals?.roas)}</td>
          </>
        )}
        {groupBy === "channel" && <td className="px-6 py-4" />}
      </tr>
    )}
  </>
);

// ---- Per-campaign cards (paid tabs) ----

// The value the backend filters on. Untagged rows are a real bucket, not an
// absence of filter, so they get an explicit sentinel (PreRegisterDAO.CAMPAIGN_NONE).
const CAMPAIGN_NONE = "__none__";
const campaignKey = (row) => row.key ?? CAMPAIGN_NONE;

// One filter tag. Name only — the numbers live in the table below, and a row
// of thirty tags is a control, not a report. The funnel still rides along in
// the tooltip for the moment you want to know whether a name is worth a click.
const CampaignTag = ({ label, tip, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    title={tip}
    className={ClassNames(
      // Borderless pills, matching the channel tabs above them: a border on
      // thirty of these reads as thirty boxes rather than one control strip.
      //
      // text-xs, NOT text-[12px]: this project runs Tailwind 2.2 without JIT
      // (no `mode: "jit"` in tailwind.config.js), so arbitrary-value classes
      // compile to no CSS at all and silently inherit the cascade. text-xs is
      // 0.75rem = the same 12px, and actually ships.
      "text-xs leading-none rounded-full px-2 py-1 truncate focus:outline-none",
      selected
        ? "font-semibold text-white"
        : "font-medium light_grayish_navy bg-gray-100 hover:bg-gray-200"
    )}
    // Same reason max-w-[12rem] is a style and not a class.
    style={{ maxWidth: "12rem", ...(selected ? { backgroundColor: "#37828B" } : {}) }}
  >
    {label}
  </button>
);

// Campaign filter for a paid tab: click a name to scope the table below to
// that campaign, click it again (or "All campaigns") to clear.
//
// Ordered by paid patients, then codes sent — not alphabetically. With thirty
// campaigns in an account, the ones worth filtering to should be the ones you
// reach first, and a dead campaign should not outrank a working one because of
// its name.
const CampaignFilter = ({ rows, isLoading, selected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  if (isLoading && rows.length === 0) return null;
  if (rows.length === 0) return null;

  const sorted = [...rows].sort(
    (a, b) => (b.paidPatients ?? 0) - (a.paidPatients ?? 0) || (b.preRegistered ?? 0) - (a.preRegistered ?? 0)
  );
  // A selected campaign always stays visible, even when it sorts below the
  // collapse point — otherwise picking a chip from "+N more" and letting the
  // row re-collapse would filter the table by something off-screen.
  const base = expanded ? sorted : sorted.slice(0, CHIPS_COLLAPSED);
  const selectedRow = selected != null && !base.some((r) => campaignKey(r) === selected)
    ? sorted.find((r) => campaignKey(r) === selected)
    : null;
  const shown = selectedRow ? [selectedRow, ...base] : base;
  const hidden = sorted.length - base.length;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-1">
      <CampaignTag
        label="All campaigns"
        tip="Every campaign in this tab"
        selected={selected == null}
        onSelect={() => onSelect(null)}
      />
      {shown.map((row) => {
        const key = campaignKey(row);
        const sent = row.preRegistered ?? 0;
        const paid = row.paidPatients ?? 0;
        return (
          <CampaignTag
            key={key}
            label={row.key || "(no campaign tagged)"}
            tip={`${row.key || "(no campaign tagged)"}\n${sent} code sent · ${paid} paid`}
            selected={selected === key}
            onSelect={() => onSelect(selected === key ? null : key)}
          />
        );
      })}
      {hidden > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs font-medium text-blue-chill hover:underline px-1"
        >
          +{hidden} more
        </button>
      )}
      {expanded && sorted.length > CHIPS_COLLAPSED && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs font-medium text-blue-chill hover:underline px-1"
        >
          Show fewer
        </button>
      )}
    </div>
  );
};

// ---- Detail table (per tab) ----

// Every captured URL param of a touch, in capture order, for the expandable
// params row. Nulls are dropped; `extra` (uncategorized query params like
// ttclid) is flattened in.
const touchParams = (touch) => {
  if (!touch) return [];
  const skip = ["kind", "capturedAt", "requestIp", "requestUserAgent", "channel", "_id"];
  const out = [];
  for (const [k, v] of Object.entries(touch)) {
    if (skip.includes(k) || v == null || v === "") continue;
    if (k === "extra" && typeof v === "object") {
      for (const [ek, ev] of Object.entries(v)) out.push([`extra.${ek}`, String(ev)]);
      continue;
    }
    out.push([k, String(v)]);
  }
  return out;
};

const StatusBadge = ({ label, on, color }) => (
  <span
    className={ClassNames(
      "inline-block px-2 py-0.5 mr-1 mb-1 rounded-full text-xs font-medium",
      on ? color : "bg-gray-100 text-gray-400"
    )}
  >
    {label}
  </span>
);

// Delivery-status badge colors (Meta effective_status values).
const STATUS_COLORS = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-amber-100 text-amber-700",
  DELETED: "bg-red-100 text-red-700",
  ARCHIVED: "bg-gray-200 text-gray-600",
};

// Numeric id plus, when the Meta API knows it, the human name and a
// delivery-status badge.
const AdIdCell = ({ id, info }) => {
  if (!id) return "—";
  if (!info) return id;
  return (
    <>
      <div className="truncate" title={id}>{info.name}</div>
      <span
        className={ClassNames(
          "inline-block px-1.5 py-0.5 rounded-full text-xs font-medium",
          STATUS_COLORS[info.status] || "bg-gray-100 text-gray-500"
        )}
      >
        {info.status}
      </span>
    </>
  );
};

// Where the traffic came from, for the Direct tab. Reads the same stored
// touch fields the Params view exposes; the distinction it draws in the top
// line is the one that matters for acting on this tab:
//   referral / email / tiktok / ... — we DO know the source, it just has no
//     tab of its own
//   direct (no referrer) — a real visit whose browser sent no Referer
//   no touch recorded — no landing-page visit at all (app-side signup)
const SourceCell = ({ row }) => {
  const s = sourceInfo(row);
  // No touch at all. `source` still separates the two very different reasons
  // that happens, and they need opposite responses: an app signup was never
  // ours to attribute, while a web visit with no touch is a capture gap.
  if (!s) {
    const fromWeb = row.source === "landing";
    return (
      <span
        className="bali_hai"
        title={
          fromWeb
            ? "Pre-registered through the marketing site, but no attribution touch was stored: the visit carried no query params AND no external referrer (typed/bookmarked URL, or an in-app link that sent no Referer), so captureAttribution recorded nothing."
            : "Created by the app's email-first signup, not the website — there was never a landing-page visit to attribute. Organic store installs and word-of-mouth land here."
        }
      >
        {fromWeb ? "web · no touch" : "app signup"}
      </span>
    );
  }
  const bare = !s.host && !s.utmSource;
  return (
    <>
      <div className="truncate capitalize" title={`channel: ${s.channel}`}>
        {s.channel}
      </div>
      <div className="text-xs bali_hai truncate" title={s.referrer || undefined}>
        {s.host
          ? s.host
          : s.utmSource
            ? `utm_source=${s.utmSource}${s.utmMedium ? ` / ${s.utmMedium}` : ""}`
            : "direct (no referrer)"}
      </div>
      {s.landingPath && (
        <div className="text-xs bali_hai truncate" title={`landed on ${s.landingPath}`}>
          {s.landingPath}
        </div>
      )}
      {/* Untagged links often carry SOMETHING (ttclid, ref, ...) and it is the
          only lead a direct-looking row gives. */}
      {bare && s.extra.length > 0 && (
        <div className="text-xs bali_hai truncate" title={s.extra.join("\n")}>
          {s.extra[0]}
          {s.extra.length > 1 ? ` +${s.extra.length - 1}` : ""}
        </div>
      )}
    </>
  );
};

// Campaign band: the aggregate row that heads each group of registrations.
//
// It spans the full detail grid rather than borrowing the detail columns,
// because a registration has no impression count and the two grains share no
// honest column. What matters is that the BANDS align with each other — fixed
// metric widths mean bounce can be scanned straight down the page, which is
// the comparison the old inline text strip made impossible.
// The campaign's aggregates, above its own table.
//
// Deliberately NOT a row in the registrations table. The two describe
// different things — a campaign has impressions, a person does not — and
// forcing them onto one grid meant either six empty cells per registration or
// merging away columns that earn their place. As its own block the summary
// lays out however reads best, and the table below keeps every original
// column and its sorting.
const CampaignSummary = ({ row, shown }) => {
  const bounce = bounceRate(row);
  // Severity, not decoration: at 90%+ essentially everyone who arrived left.
  const bounceCls =
    bounce == null ? "bg-gray-100 text-gray-500"
      : bounce >= 90 ? "bg-red-100 text-red-700"
      : bounce >= 80 ? "bg-amber-100 text-amber-700"
      : "bg-green-100 text-green-700";
  // One weight, one colour. The row badges below are colour-coded because a
  // badge is either on or off and the colour IS the state; up here every value
  // is a number, so colouring them would be decoration competing with the
  // bounce pill — which is the only place colour carries meaning.
  const Metric = ({ label, children }) => (
    <div className="flex flex-col leading-tight items-center text-center">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-semibold tabular-nums black-eclipse">{children}</span>
    </div>
  );
  // gray-100, a step darker than the gray-50 column header directly below it,
  // so the two bands read as summary-then-table rather than one block.
  //
  // The comment lives HERE, not after `return (` — a {/* */} comment is only
  // valid in children position and is a syntax error as the first thing in a
  // parenthesised expression.
  return (
    <div className="px-6 py-3 bg-gray-100 flex items-center flex-wrap gap-x-8 gap-y-2">
      {/* Inline width, not min-w-[180px]: arbitrary-value classes compile to
          nothing here (see the Tailwind/JIT note on the campaign pills). */}
      <div className="flex flex-col leading-tight mr-2" style={{ minWidth: 196 }}>
        <span className="text-sm font-semibold text-blue-chill truncate">
          {row.key || "(no campaign)"}
        </span>
      </div>
      <Metric label="Impressions">
        {row.impressions ?? 0}
        {/* Distinct people behind the pageviews, when they differ. */}
        {row.visitors != null && row.visitors !== row.impressions && (
          <span className="ml-1 text-xs font-normal bali_hai" title="Distinct visitors">{row.visitors} people</span>
        )}
      </Metric>
      {row.adClicks != null && (
        <Metric label="Ad clicks">
          <span title="Link clicks reported by the ad platform for this campaign in the same range. Should sit within tens of percent of Impressions.">{row.adClicks}</span>
        </Metric>
      )}
      {/* The same stages the row badges show, totalled. A pre-registration IS
          the code being sent, so "Code sent" is the pre-registered count under
          the name the rows below use for it. */}
      <Metric label="Code sent">
        {row.preRegistered ?? 0}
        {/* The summary covers the whole date range; the table below is only
            the current page of it. Called out beside the count it qualifies,
            rather than letting the two numbers silently disagree. */}
        {shown !== row.preRegistered && (
          <span className="ml-2 text-xs font-normal bali_hai">{shown} shown</span>
        )}
      </Metric>
      <Metric label="Verified">{row.verified ?? 0}</Metric>
      <Metric label="Registered">{row.registered ?? 0}</Metric>
      <div className="flex flex-col leading-tight items-center text-center">
        <span className="text-xs text-gray-500">Bounce</span>
        {/* No impressions means there is no rate to colour — a grey pill round
            a dash reads as a broken value. Render it like every other metric
            instead, so only real rates carry the severity pill. */}
        {bounce == null ? (
          <span className="text-xs font-semibold tabular-nums black-eclipse">0</span>
        ) : (
          <span className={`text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full ${bounceCls}`}>
            {bouncePct(row)}
          </span>
        )}
      </div>
      {/* The quiz behind the bounce: how far this campaign's visitors got,
          and why the ones who were turned away were turned away. Shown only
          when something was recorded — the beacon shipped 2026-09-03, so
          older ranges have nothing and a row of zeros would read as
          "nobody engaged" rather than "not measured". */}
      {row.quiz && (row.quiz.q1 || row.quiz.q2 || row.quiz.q3 || row.quiz.q4) ? (
        <>
          <Metric label="Q1 · Q2 · Q3 · Q4">
            <span title="People who answered each quiz question (any answer), in order">
              {row.quiz.q1} · {row.quiz.q2} · {row.quiz.q3} · {row.quiz.q4}
            </span>
          </Metric>
          <Metric label="Qualified">{row.quiz.qualified}</Metric>
          <div className="flex flex-col leading-tight items-center text-center">
            <span className="text-xs text-gray-500">Turned away</span>
            <span className="text-xs font-semibold tabular-nums black-eclipse">
              <span title="Answered No to 18 or older">{row.quiz.under18} under 18</span>
              <span className="mx-1 bali_hai font-normal">·</span>
              <span title="Chose a state InstaCured does not serve">{row.quiz.stateNotServed} state</span>
            </span>
          </div>
        </>
      ) : null}
      <Metric label="Paid">{row.paidPatients ?? 0}</Metric>
      <Metric label="Revenue">${Number(row.revenue ?? 0).toFixed(2)}</Metric>
      <Metric label="Conv %">
        {row.preRegistered
          ? `${((row.paidPatients / row.preRegistered) * 100).toFixed(1)}%`
          : "0.0%"}
      </Metric>
    </div>
  );
};

// A campaign that bought clicks and captured nothing. Stated in words because
// it is the most useful card on the page — before impressions existed it was
// indistinguishable from a campaign that never ran.
const EmptyCampaignNote = ({ impressions, visitors }) => (
  <div className="px-5 py-4 text-xs italic bali_hai">
    {impressions > 0
      ? (visitors > 0 && visitors !== impressions
        ? `${visitors} people arrived (${impressions} pageviews) and none left an email address`
        : `${impressions} people arrived and none left an email address`)
      : "No registrations in this range"}
  </div>
);

// The full page-by-page path one person took on the marketing site, in the
// order pages were first reached. A numbered list, one page per line, in a
// box that scrolls on its own (scrollbar hidden, see .scroll-hidden in
// index.css): a long journey stays readable without turning the row into a
// wall of chips, and the table row keeps a fixed height. Each
// line carries the time since the previous page and its view count; the last
// page (the exit) is bold. Exact times are in the hover.
const fmtDay = (d) => new Date(d).toLocaleDateString([], { month: "short", day: "numeric" });
const fmtTime = (d) => new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
// Elapsed time between two pageviews, at the coarsest unit that still reads
// as a number: "45s", "2m", "3h", "2d". Sub-second gaps read as "0s".
const fmtGap = (fromISO, toISO) => {
  const ms = Math.max(0, new Date(toISO).getTime() - new Date(fromISO).getTime());
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
};
// Quiz events as one short line each: "Q2 18+: no", "Turned away: under 18".
const QUIZ_LABELS = { "quiz.q1": "Q1", "quiz.q2": "Q2 18+", "quiz.q3": "Q3 state", "quiz.q4": "Q4 insurance" };
const quizEventLabel = (e) => {
  if (e.name === "email") return e.value === "app" ? "Email entered in the app \u00b7 code sent" : "Email entered \u00b7 code sent";
  if (e.name === "quiz.qualified") return "Qualified";
  if (e.name === "quiz.disqualified") {
    return e.value === "under_18" ? "Turned away: under 18" : e.value === "state_not_served" ? "Turned away: state not served" : "Turned away";
  }
  const q = QUIZ_LABELS[e.name] || e.name;
  return e.value ? `${q}: ${String(e.value).replace(/_/g, " ")}` : q;
};

const JourneyPath = ({ journey }) => {
  if (!journey || journey.length === 0) {
    return (
      <span
        className="bali_hai"
        title={"No pageview matched this registration \u2014 no visitor cookie or click id to join on (app signups never had a website visit)."}
      >
        {"\u2014"}
      </span>
    );
  }
  return (
    // Inline maxHeight, not a max-h-* class: Tailwind here runs without JIT
    // (see the campaign-pill note), so an arbitrary value would compile to
    // nothing and the box would never scroll.
    <ol
      className="text-xs list-decimal list-inside pr-2 overflow-y-auto scroll-hidden"
      style={{ maxHeight: 112, minWidth: 220 }}
      title={`${journey.length} page${journey.length === 1 ? "" : "s"}, oldest first`}
    >
      {journey.map((p, i) => {
        const last = i === journey.length - 1;
        const views = p.views ?? 1;
        if (p.kind === "event") {
          // A quiz answer, interleaved with the pages by time. Disqualified
          // answers in red: they are the reason a visit ended.
          const label = quizEventLabel(p);
          const bad = p.name === "quiz.disqualified";
          // The email step is the conversion this row exists for: green and
          // bold so it stands out from the answers around it.
          const isEmail = p.name === "email";
          return (
            <li key={`${p.name}-${p.at}-${i}`} className="py-0.5 leading-snug" title={`${p.path ?? ""}\n${fmtDay(p.at)} ${fmtTime(p.at)}`}>
              <span className={ClassNames(bad ? "text-red-600" : isEmail ? "text-green-700 font-semibold" : "text-blue-chill")}>{label}</span>
              <span className="ml-1 bali_hai whitespace-nowrap">
                {i > 0 && `+${fmtGap(journey[i - 1].at, p.at)}`}
              </span>
            </li>
          );
        }
        const tip =
          `${p.host ? p.host : ""}${p.path}\nfirst ${fmtDay(p.at)} ${fmtTime(p.at)}` +
          (p.last && p.last !== p.at ? `\nlast ${fmtDay(p.last)} ${fmtTime(p.last)}` : "") +
          `\n${views} view${views === 1 ? "" : "s"}`;
        return (
          <li key={`${p.path}-${p.at}-${i}`} className="py-0.5 leading-snug" title={tip}>
            <span className={ClassNames("black-eclipse", last && "font-semibold")}>{p.path}</span>
            {/* Time since the previous page, not a clock time: the question
                a journey answers is how long each step took. The first line
                has no previous step; exact times stay in the hover. */}
            <span className="ml-1 bali_hai whitespace-nowrap">
              {i > 0 && `+${fmtGap(journey[i - 1].at, p.at)}`}
              {views > 1 && ` \u00b7 \u00d7${views}`}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const DetailBody = ({ rows, onPreview, onHover, adInfo, showSource }) => {
  return (
    <>
      {rows.map((row) => (
        <DetailRow
          key={`${row.username}-${row.createdAt}`}
          row={row}
          onPreview={onPreview}
          onHover={onHover}
          adInfo={adInfo}
          showSource={showSource}
        />
      ))}
    </>
  );
};

const DetailRow = ({ row, onPreview, onHover, adInfo = {}, showSource = false }) => {
  const [expanded, setExpanded] = useState(false);
  // The LAST touch is the click that finally converted — it carries the ad
  // identity. The first touch is still shown in the expanded params view.
  const t = row.lastTouch || row.firstTouch || {};
  // Campaign name is not read here: the card header states it for the group.
  const campaignId = t.campaignid || row.firstTouch?.campaignid;
  const adCode = t.utmContent || row.firstTouch?.utmContent;
  const adId = t.creative || t.adgroupid || t.extra?.adid;
  const img = adImageUrl(adCode);
  const created = row.createdAt ? new Date(row.createdAt) : null;

  return (
    <>
      <tr>
        <td className="px-6 py-4 truncate text-sm">
          {created
            ? `${created.toLocaleDateString()} ${created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
            : "—"}
        </td>
        {/* The Campaign column is gone — the card header names the campaign
            once for the whole group. Its ad identifiers are NOT redundant
            though (rows in one campaign can carry different ads), so they move
            here under the email rather than disappearing with the column. */}
        <td className="px-6 py-4 truncate text-sm font-medium sapphire">
          {row.username}
          {row.stateName && (
            <div className="text-xs bali_hai font-normal">{row.stateName}</div>
          )}
          {campaignId && (
            <div className="text-xs bali_hai font-normal truncate">
              ID: <AdIdCell id={campaignId} info={adInfo[campaignId]} />
            </div>
          )}
          {adCode && (
            <div className="text-xs bali_hai font-normal truncate">Ad: {adCode}</div>
          )}
          {adId && (
            <div className="text-xs bali_hai font-normal truncate">
              Ad ID: <AdIdCell id={adId} info={adInfo[adId]} />
            </div>
          )}
        </td>
        {/* Direct tab swaps the (always empty) creative preview for the
            source/referrer of the visit. Same slot, see detailColsFor. */}
        <td className="px-6 py-4 truncate text-sm">
          {showSource ? (
            <SourceCell row={row} />
          ) : img ? (
            <button
              type="button"
              onClick={() => onPreview({ src: img, alt: adCode })}
              onMouseEnter={(e) => onHover({ src: img, ...hoverPreviewGeometry(e.clientX, e.clientY) })}
              onMouseMove={(e) => onHover({ src: img, ...hoverPreviewGeometry(e.clientX, e.clientY) })}
              onMouseLeave={() => onHover(null)}
              // The lightbox replaces the hover preview on click, and the
              // overlay must not linger once the row unmounts (paging/sorting).
              onClickCapture={() => onHover(null)}
              className="focus:outline-none"
              title="Hover to preview, click to enlarge"
            >
              <img
                src={img}
                alt={adCode}
                className="h-12 w-10 object-cover rounded border border-gray-200 cursor-zoom-in"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
            </button>
          ) : (
            <span className="text-sm bali_hai">—</span>
          )}
        </td>
        {/* First-touch device on top; a second line appears only when the
            person switched device or browser between first and last touch.
            Raw UA in the title so a row that looks wrong can be checked
            without a DB query. */}
        <td className="px-6 py-4 truncate text-sm" title={uaOf(row) || undefined}>
          {(() => {
            const j = uaJourney(row);
            if (!j) return <span className="bali_hai">—</span>;
            return (
              <>
                <div className="truncate">{j.first.device} · {j.first.browser}</div>
                {j.last && (
                  <div className="text-xs bali_hai truncate" title="Switched device between first and last touch">
                    → {j.last.device} · {j.last.browser}
                  </div>
                )}
              </>
            );
          })()}
        </td>
        <td className="px-6 py-4 text-sm" style={{ whiteSpace: "normal" }}>
          <JourneyPath journey={row.journey} />
        </td>
        <td className="px-6 py-4 truncate text-sm">
          <StatusBadge
            label="Code sent"
            on={Boolean(row.codeSentAt)}
            color="bg-amber-100 text-amber-700"
          />
          <StatusBadge label="Verified" on={row.emailVerified} color="bg-blue-100 text-blue-700" />
          <StatusBadge label="Registered" on={row.registered} color="bg-indigo-100 text-indigo-700" />
          <StatusBadge
            label={row.paidVisits > 0 ? `Paid $${Number(row.revenue ?? 0).toFixed(0)}` : "Paid"}
            on={row.paidVisits > 0}
            color="bg-green-100 text-green-700"
          />
          {row.discountOffered && (
            <StatusBadge
              label={row.discountRedeemedAt ? "10% used" : "10% offered"}
              on
              color="bg-amber-100 text-amber-700"
            />
          )}
          {/* The lapsed-registration nudge, and whether a payment followed it.
              Shown as its own badge rather than folded into "10% offered":
              the discount may have come from the landing funnel instead, and
              the two answer different questions. */}
          {row.followUpOfferSentAt && (
            <StatusBadge
              label={
                // The FIRST PAID visit date, not the pre-registration date:
                // the latter is when they asked for a code, which says nothing
                // about whether the payment followed the nudge.
                row.firstPaidAt && new Date(row.firstPaidAt) > new Date(row.followUpOfferSentAt)
                  ? "Nudged → paid"
                  : "Nudged"
              }
              on
              color="bg-purple-100 text-purple-700"
            />
          )}
        </td>
        <td className="px-6 py-4 truncate text-sm">
          {row.postback ? (
            <>
              <DeliveryChip label="Meta" p={row.postback.meta} judged={row.postback} />
              <DeliveryChip label="Google" p={row.postback.google} judged={row.postback} />
            </>
          ) : (
            <span className="bali_hai" title="No first-party conversion recorded yet (no paid visit, or the postback runner hasn't run)">—</span>
          )}
        </td>
        <td className="px-6 py-4 truncate text-sm">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-blue-chill underline focus:outline-none"
          >
            {expanded ? "Hide" : "View"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={DETAIL_COLS.length} className="px-6 py-4 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[["First touch", row.firstTouch], ["Last touch", row.lastTouch]].map(
                ([label, touch]) =>
                  touch && (
                    <div key={label}>
                      <div className="text-xs font-semibold sapphire uppercase mb-1">
                        {label}
                        {touch.channel && (
                          <span className="ml-2 normal-case bali_hai font-normal">
                            channel: {touch.channel}
                          </span>
                        )}
                      </div>
                      <table className="text-xs">
                        <tbody>
                          {touchParams(touch).map(([k, v]) => (
                            <tr key={k}>
                              <td className="pr-3 py-0.5 bali_hai whitespace-nowrap align-top">{k}</td>
                              <td className="py-0.5 break-all">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
